// src/pages/SuperAdminPage.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import superadminService from '../services/superadminService';
import billingService from '../services/billingService';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmModal from '../components/ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PLAN_TIERS = ['standard', 'pro', 'enterprise', 'custom'];

function SuperAdminPage() {
  const [companies, setCompanies]     = useState([]);
  const [registry, setRegistry]       = useState([]);
  const [loading, setLoading]         = useState(true);

  // Selected company state
  const [selectedId, setSelectedId]         = useState(null);
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesSaving, setModulesSaving]   = useState(false);
  const [modulesSuccess, setModulesSuccess] = useState(false);
  const [modulesError, setModulesError]     = useState('');

  // Edit selected company
  const [editName, setEditName]         = useState('');
  const [editDomain, setEditDomain]     = useState('');
  const [editPlanTier, setEditPlanTier] = useState('standard');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editMaxConcurrent, setEditMaxConcurrent] = useState(5);
  const [editDailyBudget, setEditDailyBudget]     = useState('');
  const [editMonthlyBudget, setEditMonthlyBudget] = useState('');
  const [editSaving, setEditSaving]     = useState(false);
  const [editSuccess, setEditSuccess]   = useState(false);

  // Reseller link (docs/RESELLER_MODULE.md) — is this company a reseller of another?
  const [resellerParentCompanyId, setResellerParentCompanyId]   = useState('');
  const [resellerParentCustomerId, setResellerParentCustomerId] = useState('');
  const [resellerParentCustomers, setResellerParentCustomers]   = useState([]);
  const [resellerSaving, setResellerSaving]   = useState(false);
  const [resellerSuccess, setResellerSuccess] = useState(false);
  const [resellerError, setResellerError]     = useState('');

  // AI usage overview
  const [aiUsageData, setAiUsageData]     = useState([]);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);

  // New company form
  const [newName, setNewName]     = useState('');
  const [newPlan, setNewPlan]     = useState('standard');
  const [creating, setCreating]   = useState(false);
  const [createError, setCreateError] = useState('');

  // Company Documents
  const [documents, setDocuments]     = useState([]);
  const [docLabel, setDocLabel]       = useState('');
  const [docFile, setDocFile]         = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError]       = useState('');
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);

  // Users
  const [users, setUsers]             = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [userCreating, setUserCreating] = useState(false);
  const [userError, setUserError]     = useState('');

  // Billing
  const [billing, setBilling]           = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [billingAction, setBillingAction] = useState(null); // 'subscribing' | 'canceling' | 'reactivating' | 'portal'
  const [subscribeForm, setSubscribeForm] = useState(false);
  const [subscribeTier, setSubscribeTier] = useState('standard');
  const [subscribeCustomPrice, setSubscribeCustomPrice] = useState('');

  // Reset password (for company users)
  const [resetUserId, setResetUserId]   = useState(null);
  const [resetPw, setResetPw]           = useState('');
  const [resetSaving, setResetSaving]   = useState(false);
  const [resetError, setResetError]     = useState('');

  // My Account — change own password
  const [myCurrentPw, setMyCurrentPw]       = useState('');
  const [myNewPw, setMyNewPw]               = useState('');
  const [myPwSaving, setMyPwSaving]         = useState(false);
  const [myPwMsg, setMyPwMsg]               = useState(null); // { type: 'success'|'error', text }
  const [showCurrentPw, setShowCurrentPw]   = useState(false);
  const [showNewPw, setShowNewPw]           = useState(false);

  // My Account — MFA
  const [mfaEnabled, setMfaEnabled]     = useState(false);
  const [mfaQr, setMfaQr]               = useState(null);
  const [mfaCode, setMfaCode]           = useState('');
  const [mfaMsg, setMfaMsg]             = useState(null);
  const [mfaLoading, setMfaLoading]     = useState(false);

  useEffect(() => {
    Promise.all([
      superadminService.getCompanies(),
      superadminService.getRegistry(),
      axios.get(`${API_URL}/api/superadmin/me`, { headers: authHeader() }),
    ]).then(([c, r, me]) => {
      setCompanies(c);
      setRegistry(r);
      setMfaEnabled(me.data.totpEnabled);
    }).finally(() => setLoading(false));

    setAiUsageLoading(true);
    superadminService.getAiUsage()
      .then(setAiUsageData)
      .catch(() => {})
      .finally(() => setAiUsageLoading(false));
  }, []);

  const loadBilling = async (id) => {
    setBillingLoading(true);
    setBillingError('');
    setBilling(null);
    setSubscribeForm(false);
    try {
      const b = await billingService.getCompanyBilling(id);
      setBilling(b);
      setSubscribeTier(b.planTier || 'standard');
      setSubscribeCustomPrice(b.planTier === 'custom' && b.priceUnitAmount ? String(b.priceUnitAmount / 100) : '');
    } catch (err) {
      setBillingError(err.response?.data?.error || 'Failed to load billing info');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (subscribeTier === 'custom' && !(Number(subscribeCustomPrice) > 0)) {
      setBillingError('Enter a custom price greater than $0');
      return;
    }
    setBillingAction('subscribing');
    setBillingError('');
    try {
      await billingService.subscribeCompany(selectedId, subscribeTier, subscribeCustomPrice);
      setSubscribeForm(false);
      await loadBilling(selectedId);
    } catch (err) {
      setBillingError(err.response?.data?.error || 'Failed to subscribe');
    } finally {
      setBillingAction(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel subscription at end of billing period?')) return;
    setBillingAction('canceling');
    setBillingError('');
    try {
      await billingService.cancelSubscription(selectedId);
      await loadBilling(selectedId);
    } catch (err) {
      setBillingError(err.response?.data?.error || 'Failed to cancel');
    } finally {
      setBillingAction(null);
    }
  };

  const handleReactivate = async () => {
    setBillingAction('reactivating');
    setBillingError('');
    try {
      await billingService.reactivateSubscription(selectedId);
      await loadBilling(selectedId);
    } catch (err) {
      setBillingError(err.response?.data?.error || 'Failed to reactivate');
    } finally {
      setBillingAction(null);
    }
  };

  const handleOpenPortal = async () => {
    setBillingAction('portal');
    setBillingError('');
    try {
      const url = await billingService.getPortalUrl(selectedId);
      window.open(url, '_blank');
    } catch (err) {
      setBillingError(err.response?.data?.error || 'Failed to open portal');
    } finally {
      setBillingAction(null);
    }
  };

  const selectCompany = async (company) => {
    setSelectedId(company.id);
    setEditName(company.name);
    setEditDomain(company.domain || '');
    setEditPlanTier(company.planTier || 'standard');
    setEditIsActive(company.isActive !== false);
    setEditMaxConcurrent(company.maxConcurrentRequests ?? 5);
    setEditDailyBudget(company.aiDailyBudgetUsd != null ? String(company.aiDailyBudgetUsd) : '');
    setEditMonthlyBudget(company.aiMonthlyBudgetUsd != null ? String(company.aiMonthlyBudgetUsd) : '');
    setModulesSuccess(false);
    setModulesError('');
    setEditSuccess(false);
    setResellerSuccess(false);
    setResellerError('');
    setResellerParentCustomers([]);
    loadBilling(company.id);
    setModulesLoading(true);
    setUsers([]);
    setUserError('');
    setNewUsername('');
    setNewPassword('');
    setDocuments([]);
    setDocError('');
    setDocLabel('');
    setDocFile(null);
    try {
      const [{ moduleKeys }, companyUsers, resellerLink, companyDocs] = await Promise.all([
        superadminService.getModules(company.id),
        superadminService.getCompanyUsers(company.id),
        superadminService.getResellerLink(company.id),
        superadminService.getCompanyDocuments(company.id),
      ]);
      setSelectedModules(new Set(moduleKeys));
      setUsers(companyUsers);
      setDocuments(companyDocs);
      setResellerParentCompanyId(resellerLink.parentCompanyId ? String(resellerLink.parentCompanyId) : '');
      setResellerParentCustomerId(resellerLink.parentCustomerId ? String(resellerLink.parentCustomerId) : '');
      if (resellerLink.parentCompanyId) {
        superadminService.getCompanyCustomers(resellerLink.parentCompanyId).then(setResellerParentCustomers).catch(() => {});
      }
    } finally {
      setModulesLoading(false);
    }
  };

  const handleParentCompanyChange = (value) => {
    setResellerParentCompanyId(value);
    setResellerParentCustomerId('');
    setResellerParentCustomers([]);
    if (value) {
      superadminService.getCompanyCustomers(value).then(setResellerParentCustomers).catch(() => {});
    }
  };

  const handleResellerSave = async () => {
    setResellerSaving(true);
    setResellerError('');
    try {
      if (!resellerParentCompanyId) {
        await superadminService.setResellerLink(selectedId, null, null);
      } else {
        if (!resellerParentCustomerId) {
          setResellerError('Select the Customer record representing this company in the parent tenant.');
          return;
        }
        await superadminService.setResellerLink(selectedId, resellerParentCompanyId, resellerParentCustomerId);
      }
      setResellerSuccess(true);
      setTimeout(() => setResellerSuccess(false), 3000);
    } catch (err) {
      setResellerError(err.response?.data?.error || 'Failed to save reseller link');
    } finally {
      setResellerSaving(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      const c = await superadminService.createCompany(newName.trim(), newPlan);
      setCompanies(prev => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const updated = await superadminService.updateCompany(selectedId, {
        name: editName, domain: editDomain.trim() || null, planTier: editPlanTier, isActive: editIsActive,
        maxConcurrentRequests: parseInt(editMaxConcurrent, 10) || 5,
        aiDailyBudgetUsd:   editDailyBudget   !== '' ? parseFloat(editDailyBudget)   : null,
        aiMonthlyBudgetUsd: editMonthlyBudget !== '' ? parseFloat(editMonthlyBudget) : null,
      });
      setCompanies(prev => prev.map(c => c.id === selectedId ? { ...c, ...updated } : c));
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
    } catch {} finally { setEditSaving(false); }
  };

  const toggleModule = (key) => {
    setSelectedModules(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedModules(new Set(registry.map(m => m.key)));
  const selectNone = () => setSelectedModules(new Set());

  const handleModulesSave = async () => {
    setModulesSaving(true);
    setModulesError('');
    try {
      await superadminService.setModules(selectedId, [...selectedModules]);
      setCompanies(prev => prev.map(c => c.id === selectedId ? { ...c, moduleCount: selectedModules.size } : c));
      setModulesSuccess(true);
      setTimeout(() => setModulesSuccess(false), 3000);
    } catch (err) {
      // Previously a silent `catch {}` — a failed save looked identical to a successful one,
      // with no indication anything was wrong. Confirmed in production: a module grant that
      // never persisted left no trace until a direct DB check surfaced it.
      setModulesError(err.response?.data?.error || err.message || 'Failed to save modules.');
    } finally { setModulesSaving(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserCreating(true);
    setUserError('');
    try {
      const user = await superadminService.createAdminUser(selectedId, newUsername.trim(), newPassword);
      setUsers(prev => [...prev, user]);
      setNewUsername('');
      setNewPassword('');
    } catch (err) {
      setUserError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setUserCreating(false);
    }
  };

  const handleToggleUser = async (userId, isActive) => {
    await superadminService.toggleUserActive(selectedId, userId, isActive);
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive } : u));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetSaving(true);
    setResetError('');
    try {
      await superadminService.resetUserPassword(selectedId, resetUserId, resetPw);
      setResetUserId(null);
      setResetPw('');
    } catch (err) {
      setResetError(err.response?.data?.error || 'Failed to update password');
    } finally {
      setResetSaving(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!docFile) { setDocError('Choose a file first.'); return; }
    setDocUploading(true);
    setDocError('');
    try {
      const { uploadUrl, publicUrl, key } = await superadminService.getCompanyDocumentUploadUrl(selectedId, docFile.name, docFile.type, docFile.size);
      await superadminService.uploadCompanyDocumentToS3(uploadUrl, docFile);
      const doc = await superadminService.saveCompanyDocument(selectedId, {
        label: docLabel.trim() || docFile.name,
        fileKey: key,
        fileUrl: publicUrl,
        fileName: docFile.name,
        fileSize: docFile.size,
        mimeType: docFile.type,
      });
      setDocuments(prev => [doc, ...prev]);
      setDocLabel('');
      setDocFile(null);
    } catch (err) {
      setDocError(err?.response?.data?.error || 'Failed to upload document.');
    } finally {
      setDocUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    await superadminService.deleteCompanyDocument(selectedId, deleteDocTarget.id);
    setDocuments(prev => prev.filter(d => d.id !== deleteDocTarget.id));
    setDeleteDocTarget(null);
  };

  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    setMyPwSaving(true);
    setMyPwMsg(null);
    try {
      await axios.patch(`${API_URL}/api/superadmin/me/password`, { currentPassword: myCurrentPw, newPassword: myNewPw }, { headers: authHeader() });
      setMyPwMsg({ type: 'success', text: 'Password updated' });
      setMyCurrentPw(''); setMyNewPw('');
    } catch (err) {
      setMyPwMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update password' });
    } finally { setMyPwSaving(false); }
  };

  const handleMfaSetup = async () => {
    setMfaLoading(true); setMfaMsg(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/me/totp/setup`, { headers: authHeader() });
      setMfaQr(data.qrDataUrl);
      setMfaCode('');
    } catch { setMfaMsg({ type: 'error', text: 'Failed to generate QR code' }); }
    finally { setMfaLoading(false); }
  };

  const handleMfaEnable = async (e) => {
    e.preventDefault();
    setMfaLoading(true); setMfaMsg(null);
    try {
      await axios.post(`${API_URL}/api/superadmin/me/totp/enable`, { code: mfaCode }, { headers: authHeader() });
      setMfaEnabled(true); setMfaQr(null); setMfaCode('');
      setMfaMsg({ type: 'success', text: 'MFA enabled successfully' });
    } catch (err) {
      setMfaMsg({ type: 'error', text: err.response?.data?.error || 'Failed to enable MFA' });
    } finally { setMfaLoading(false); }
  };

  const handleMfaDisable = async (e) => {
    e.preventDefault();
    setMfaLoading(true); setMfaMsg(null);
    try {
      await axios.post(`${API_URL}/api/superadmin/me/totp/disable`, { code: mfaCode }, { headers: authHeader() });
      setMfaEnabled(false); setMfaCode('');
      setMfaMsg({ type: 'success', text: 'MFA disabled' });
    } catch (err) {
      setMfaMsg({ type: 'error', text: err.response?.data?.error || 'Failed to disable MFA' });
    } finally { setMfaLoading(false); }
  };

  const categories = [...new Set(registry.map(m => m.category))];
  const byCategory = categories.map(cat => ({ cat, modules: registry.filter(m => m.category === cat) }));
  const selectedCompany = companies.find(c => c.id === selectedId);

  if (loading) return <div className="container mt-4"><div className="spinner-border" role="status" /></div>;

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 1140 }}>
      <h4 className="mb-4">Super Admin — Companies & Modules</h4>
      {/* ── My Account ── */}
      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title mb-3">Change Password</h6>
              <form onSubmit={handleChangeMyPassword}>
                <div className="mb-2">
                  <div className="input-group input-group-sm">
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      className="form-control"
                      placeholder="Current password"
                      value={myCurrentPw}
                      onChange={e => setMyCurrentPw(e.target.value)}
                      required
                    />
                    <button type="button" className="btn btn-outline-secondary" tabIndex={-1} onClick={() => setShowCurrentPw(v => !v)}>
                      {showCurrentPw ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <div className="mb-2">
                  <div className="input-group input-group-sm">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      className="form-control"
                      placeholder="New password (min 6 chars)"
                      value={myNewPw}
                      onChange={e => setMyNewPw(e.target.value)}
                      required
                    />
                    <button type="button" className="btn btn-outline-secondary" tabIndex={-1} onClick={() => setShowNewPw(v => !v)}>
                      {showNewPw ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={myPwSaving}>
                  {myPwSaving ? <span className="spinner-border spinner-border-sm" /> : 'Update Password'}
                </button>
                {myPwMsg && (
                  <div className={`small mt-2 text-${myPwMsg.type === 'success' ? 'success' : 'danger'}`}>
                    {myPwMsg.text}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-title mb-1">Two-Factor Authentication</h6>
              <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                Status: <span className={`fw-semibold ${mfaEnabled ? 'text-success' : 'text-secondary'}`}>
                  {mfaEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {!mfaEnabled && !mfaQr && (
                <button className="btn btn-outline-primary btn-sm" onClick={handleMfaSetup} disabled={mfaLoading}>
                  {mfaLoading ? <span className="spinner-border spinner-border-sm" /> : 'Set up MFA'}
                </button>
              )}

              {!mfaEnabled && mfaQr && (
                <div>
                  <p className="small text-muted mb-2">Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.</p>
                  <img src={mfaQr} alt="QR code" style={{ width: 160, height: 160 }} className="mb-2 d-block" />
                  <form onSubmit={handleMfaEnable} className="d-flex gap-2 align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ width: 120 }}
                      placeholder="000000"
                      value={mfaCode}
                      onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      autoFocus
                    />
                    <button type="submit" className="btn btn-success btn-sm" disabled={mfaLoading || mfaCode.length !== 6}>
                      Activate
                    </button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setMfaQr(null)}>
                      Cancel
                    </button>
                  </form>
                </div>
              )}

              {mfaEnabled && (
                <form onSubmit={handleMfaDisable} className="d-flex gap-2 align-items-center flex-wrap">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ width: 120 }}
                    placeholder="Enter code to disable"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                  />
                  <button type="submit" className="btn btn-outline-danger btn-sm" disabled={mfaLoading || mfaCode.length !== 6}>
                    Disable MFA
                  </button>
                </form>
              )}

              {mfaMsg && (
                <div className={`small mt-2 text-${mfaMsg.type === 'success' ? 'success' : 'danger'}`}>
                  {mfaMsg.text}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">

        {/* ── Left: company list ── */}
        <div className="col-lg-4">
          {/* New company */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">New Company</h6>
              <form onSubmit={handleCreateCompany}>
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="Company name"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  required
                />
                <select
                  className="form-select form-select-sm mb-2"
                  value={newPlan}
                  onChange={e => setNewPlan(e.target.value)}
                >
                  {PLAN_TIERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {createError && (
                  <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{createError}</div>
                )}
                <button className="btn btn-primary btn-sm w-100" disabled={creating}>
                  {creating ? <span className="spinner-border spinner-border-sm" /> : '+ Create Company'}
                </button>
              </form>
            </div>
          </div>

          {/* List */}
          <div className="list-group">
            {companies.map(c => (
              <button
                key={c.id}
                className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start ${selectedId === c.id ? 'active' : ''}`}
                onClick={() => selectCompany(c)}
              >
                <div>
                  <div className="fw-semibold" style={{ fontSize: 14 }}>{c.name}</div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>
                    {c.planTier || 'standard'} · {c.moduleCount === 0 ? 'all (default)' : `${c.moduleCount} modules`}
                  </div>
                </div>
                <span
                  className={`badge ms-2 mt-1 ${c.isActive !== false ? 'bg-success' : 'bg-secondary'}`}
                  style={{ fontSize: 10 }}
                >
                  {c.isActive !== false ? 'active' : 'inactive'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Right: detail + modules ── */}
        <div className="col-lg-8">
          {!selectedCompany ? (
            <div className="card">
              <div className="card-body text-muted text-center py-5" style={{ fontSize: 14 }}>
                Select a company to manage its settings and modules.
              </div>
            </div>
          ) : (
            <>
              {/* Company edit */}
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-3">{selectedCompany.name}</h6>
                  <form onSubmit={handleEditSave}>
                    <div className="row g-2 align-items-end mb-2">
                      <div className="col-md-7">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>Name</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="row g-2 align-items-end">
                      <div className="col-md-5">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>
                          Domain <span className="text-muted fw-normal">(hostname only)</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. pizzacini.quicklog.app"
                          value={editDomain}
                          onChange={e => setEditDomain(e.target.value)}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>Plan</label>
                        <select
                          className="form-select form-select-sm"
                          value={editPlanTier}
                          onChange={e => setEditPlanTier(e.target.value)}
                        >
                          {PLAN_TIERS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>
                          Max concurrent req.
                          <span className="text-muted ms-1" style={{ fontSize: 11 }} title="Max simultaneous API requests this company can have in-flight. Raise when upgrading DB plan.">ⓘ</span>
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={1} max={50}
                          value={editMaxConcurrent}
                          onChange={e => setEditMaxConcurrent(e.target.value)}
                        />
                      </div>
                      <div className="col-md-2 d-flex align-items-center gap-2 pb-1">
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          id="chk-active"
                          checked={editIsActive}
                          onChange={e => setEditIsActive(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="chk-active" style={{ fontSize: 13 }}>Active</label>
                      </div>
                    </div>
                    <div className="row g-2 align-items-end mt-1">
                      <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>
                          Daily AI budget (USD)
                          <span className="text-muted ms-1" style={{ fontSize: 11 }} title="Hard block per day. Leave blank for unlimited.">ⓘ</span>
                        </label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input
                            type="number" min="0" step="0.5"
                            className="form-control form-control-sm"
                            placeholder="unlimited"
                            value={editDailyBudget}
                            onChange={e => setEditDailyBudget(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>
                          Monthly AI budget (USD)
                          <span className="text-muted ms-1" style={{ fontSize: 11 }} title="Hard block per calendar month. Leave blank for unlimited.">ⓘ</span>
                        </label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input
                            type="number" min="0" step="1"
                            className="form-control form-control-sm"
                            placeholder="unlimited"
                            value={editMonthlyBudget}
                            onChange={e => setEditMonthlyBudget(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <button type="submit" className="btn btn-primary btn-sm w-100" disabled={editSaving}>
                          {editSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                        </button>
                      </div>
                    </div>
                    {editSuccess && <div className="text-success small mt-2">Saved</div>}
                  </form>
                </div>
              </div>

              {/* ── Reseller Link (docs/RESELLER_MODULE.md) ── */}
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-1">Reseller Link</h6>
                  <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                    If this company resells another company's products (e.g. DM2P reselling
                    Pizzacini's), link it here. Every order its customers place will also auto-create
                    a mirrored order in the parent company at the wholesale price, tagged with the
                    Customer record below.
                  </p>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-5">
                      <label className="form-label mb-1" style={{ fontSize: 13 }}>Parent Company</label>
                      <SearchableSelect
                        options={companies.filter(c => c.id !== selectedId).map(c => ({ value: c.id, label: c.name }))}
                        value={resellerParentCompanyId}
                        onChange={handleParentCompanyChange}
                        placeholder="None — not a reseller"
                        size="sm"
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label mb-1" style={{ fontSize: 13 }}>
                        Parent Customer <span className="text-muted fw-normal">(this company, as a customer of the parent)</span>
                      </label>
                      <SearchableSelect
                        options={resellerParentCustomers.map(c => ({ value: c.id, label: c.name }))}
                        value={resellerParentCustomerId}
                        onChange={setResellerParentCustomerId}
                        placeholder={resellerParentCompanyId ? 'Select customer' : 'Pick a parent company first'}
                        disabled={!resellerParentCompanyId}
                        size="sm"
                      />
                    </div>
                    <div className="col-md-2">
                      <button type="button" className="btn btn-primary btn-sm w-100" onClick={handleResellerSave} disabled={resellerSaving}>
                        {resellerSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                      </button>
                    </div>
                  </div>
                  {resellerError && <div className="text-danger small mt-2">{resellerError}</div>}
                  {resellerSuccess && <div className="text-success small mt-2">Saved</div>}
                </div>
              </div>

              {/* ── Billing ── */}
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="card-title mb-0">Billing & Subscription</h6>
                      {billing?.billingExempt && (
                        <span className="badge bg-success" style={{ fontSize: 10 }}>EXEMPT</span>
                      )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      {/* Billing exemption toggle */}
                      {billing && (
                        <div className="form-check form-switch mb-0 d-flex align-items-center gap-1" style={{ fontSize: 12 }}>
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            id="chk-exempt"
                            checked={!!billing.billingExempt}
                            onChange={async (e) => {
                              const val = e.target.checked;
                              try {
                                await billingService.setExempt(selectedId, val);
                                setBilling(b => ({ ...b, billingExempt: val }));
                              } catch {
                                setBillingError('Failed to update exemption');
                              }
                            }}
                          />
                          <label className="form-check-label text-muted" htmlFor="chk-exempt">
                            Billing exempt (free access)
                          </label>
                        </div>
                      )}
                      {billing?.stripeCustomerId && (
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleOpenPortal}
                          disabled={billingAction === 'portal'}
                        >
                          {billingAction === 'portal'
                            ? <span className="spinner-border spinner-border-sm" />
                            : '↗ Customer Portal'}
                        </button>
                      )}
                    </div>
                  </div>

                  {billingError && (
                    <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>
                      {billingError}
                      <button className="btn-close btn-sm float-end" onClick={() => setBillingError('')} />
                    </div>
                  )}

                  {billingLoading ? (
                    <div className="text-center py-2 text-muted">
                      <span className="spinner-border spinner-border-sm me-2" />Loading billing…
                    </div>
                  ) : billing ? (() => {
                    const status = billing.stripeStatus || billing.subscriptionStatus;
                    const hasSub = !!billing.stripeSubscriptionId;
                    const trialDate = billing.trialEndsAt ? new Date(billing.trialEndsAt) : null;
                    const trialActive = trialDate && trialDate > new Date();
                    const trialDaysLeft = trialDate ? Math.ceil((trialDate - Date.now()) / 86400000) : null;
                    const periodEnd = billing.currentPeriodEnd ? new Date(billing.currentPeriodEnd) : null;

                    const statusBadge = (s) => {
                      const map = {
                        active:   'bg-success',
                        trialing: 'bg-info',
                        past_due: 'bg-danger',
                        canceled: 'bg-secondary',
                        incomplete: 'bg-warning text-dark',
                        incomplete_expired: 'bg-danger',
                        unpaid: 'bg-danger',
                      };
                      return <span className={`badge ${map[s] || 'bg-secondary'} ms-2`} style={{ fontSize: 11 }}>{s}</span>;
                    };

                    return (
                      <div>
                        {/* Status row */}
                        <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: 13 }}>
                          <div>
                            <span className="text-muted">Plan:</span>{' '}
                            <span className="fw-semibold text-capitalize">{billing.planTier || '—'}</span>
                          </div>
                          {billing.priceUnitAmount != null && (
                            <div>
                              <span className="text-muted">Price:</span>{' '}
                              <span className="fw-semibold">
                                ${(billing.priceUnitAmount / 100).toFixed(2)}{billing.priceInterval ? `/${billing.priceInterval}` : ''}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="text-muted">Status:</span>
                            {hasSub ? statusBadge(status)
                              : trialActive
                                ? <span className="badge bg-info ms-2" style={{ fontSize: 11 }}>trial</span>
                                : <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 11 }}>no subscription</span>
                            }
                          </div>
                          {trialActive && (
                            <div>
                              <span className="text-muted">Trial ends:</span>{' '}
                              <span className={`fw-semibold ${trialDaysLeft <= 3 ? 'text-danger' : ''}`}>
                                {trialDate.toLocaleDateString()} ({trialDaysLeft}d left)
                              </span>
                            </div>
                          )}
                          {periodEnd && hasSub && (
                            <div>
                              <span className="text-muted">
                                {billing.cancelAtPeriodEnd ? 'Cancels:' : 'Next billing:'}
                              </span>{' '}
                              <span className={`fw-semibold ${billing.cancelAtPeriodEnd ? 'text-warning' : ''}`}>
                                {periodEnd.toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {billing.stripeCustomerId && (
                            <div>
                              <span className="text-muted">Stripe ID:</span>{' '}
                              <span className="text-muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                                {billing.stripeCustomerId}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                          {/* Subscribe / Change Plan */}
                          {!subscribeForm ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setSubscribeForm(true)}
                            >
                              {hasSub ? 'Change Plan' : '+ Subscribe'}
                            </button>
                          ) : (
                            <div className="d-flex gap-2 align-items-center">
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 160 }}
                                value={subscribeTier}
                                onChange={e => setSubscribeTier(e.target.value)}
                              >
                                {PLAN_TIERS.map(t => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                              {subscribeTier === 'custom' && (
                                <div className="input-group input-group-sm" style={{ width: 130 }}>
                                  <span className="input-group-text">$</span>
                                  <input
                                    type="number"
                                    className="form-control"
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                    value={subscribeCustomPrice}
                                    onChange={e => setSubscribeCustomPrice(e.target.value)}
                                  />
                                  <span className="input-group-text">/mo</span>
                                </div>
                              )}
                              <button
                                className="btn btn-success btn-sm"
                                onClick={handleSubscribe}
                                disabled={billingAction === 'subscribing'}
                              >
                                {billingAction === 'subscribing'
                                  ? <span className="spinner-border spinner-border-sm" />
                                  : hasSub ? 'Update' : 'Subscribe'}
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setSubscribeForm(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          )}

                          {/* Cancel / Reactivate */}
                          {hasSub && !billing.cancelAtPeriodEnd && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={handleCancelSubscription}
                              disabled={billingAction === 'canceling'}
                            >
                              {billingAction === 'canceling'
                                ? <span className="spinner-border spinner-border-sm" />
                                : 'Cancel at Period End'}
                            </button>
                          )}
                          {hasSub && billing.cancelAtPeriodEnd && (
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={handleReactivate}
                              disabled={billingAction === 'reactivating'}
                            >
                              {billingAction === 'reactivating'
                                ? <span className="spinner-border spinner-border-sm" />
                                : 'Undo Cancellation'}
                            </button>
                          )}
                        </div>

                        {/* Incomplete subscription warning */}
                        {hasSub && (status === 'incomplete' || status === 'past_due') && (
                          <div className="alert alert-warning py-2 mt-3 mb-0" style={{ fontSize: 12 }}>
                            Payment is required to activate this subscription. Use <strong>Customer Portal</strong> to add a payment method.
                          </div>
                        )}
                      </div>
                    );
                  })() : null}
                </div>
              </div>

              {/* Admin Users */}
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-3">Admin Users</h6>

                  {/* Existing users */}
                  {users.length > 0 && (
                    <table className="table table-sm mb-3">
                      <thead className="table-light">
                        <tr>
                          <th>Username</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <React.Fragment key={u.id}>
                            <tr>
                              <td style={{ fontSize: 13 }}>{u.username}</td>
                              <td><span className="badge bg-secondary" style={{ fontSize: 11 }}>{u.role}</span></td>
                              <td>
                                <span className={`badge ${u.isActive ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 11 }}>
                                  {u.isActive ? 'active' : 'inactive'}
                                </span>
                              </td>
                              <td className="d-flex gap-1">
                                <button
                                  className={`btn btn-sm ${u.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                  style={{ fontSize: 11, padding: '1px 8px' }}
                                  onClick={() => handleToggleUser(u.id, !u.isActive)}
                                >
                                  {u.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  style={{ fontSize: 11, padding: '1px 8px' }}
                                  onClick={() => { setResetUserId(resetUserId === u.id ? null : u.id); setResetPw(''); setResetError(''); }}
                                >
                                  Reset PW
                                </button>
                              </td>
                            </tr>
                            {resetUserId === u.id && (
                              <tr>
                                <td colSpan={4} className="bg-light">
                                  <form onSubmit={handleResetPassword} className="d-flex gap-2 align-items-center p-1">
                                    <input
                                      type="password"
                                      className="form-control form-control-sm"
                                      style={{ width: 180 }}
                                      placeholder="New password"
                                      value={resetPw}
                                      onChange={e => setResetPw(e.target.value)}
                                      required
                                      autoFocus
                                    />
                                    <button type="submit" className="btn btn-primary btn-sm" disabled={resetSaving}>
                                      {resetSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save'}
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setResetUserId(null)}>
                                      Cancel
                                    </button>
                                    {resetError && <span className="text-danger" style={{ fontSize: 12 }}>{resetError}</span>}
                                  </form>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Create admin user */}
                  <form onSubmit={handleCreateUser} className="d-flex gap-2 flex-wrap align-items-end">
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Username</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: 160 }}
                        value={newUsername}
                        onChange={e => setNewUsername(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Temp Password</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: 160 }}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={userCreating}>
                      {userCreating ? <span className="spinner-border spinner-border-sm" /> : '+ Add Admin'}
                    </button>
                  </form>
                  {userError && <div className="text-danger small mt-2">{userError}</div>}
                </div>
              </div>

              {/* Company Documents */}
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title mb-3">Company Documents</h6>

                  {documents.length > 0 && (
                    <table className="table table-sm mb-3">
                      <thead className="table-light">
                        <tr>
                          <th>Label</th>
                          <th>File</th>
                          <th>Size</th>
                          <th>Uploaded</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map(d => (
                          <tr key={d.id}>
                            <td style={{ fontSize: 13 }}>{d.label}</td>
                            <td style={{ fontSize: 13 }}>
                              <a href={d.fileUrl} target="_blank" rel="noreferrer">{d.fileName}</a>
                            </td>
                            <td style={{ fontSize: 13 }}>{d.fileSize ? `${(d.fileSize / 1024).toFixed(0)} KB` : '—'}</td>
                            <td style={{ fontSize: 13 }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                style={{ fontSize: 11, padding: '1px 8px' }}
                                onClick={() => setDeleteDocTarget(d)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <form onSubmit={handleUploadDocument} className="d-flex gap-2 flex-wrap align-items-end">
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>Label</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: 220 }}
                        placeholder="e.g. Signed MSA — 2026-01-15"
                        value={docLabel}
                        onChange={e => setDocLabel(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: 12 }}>File (PDF/JPG/PNG)</label>
                      <input
                        type="file"
                        className="form-control form-control-sm"
                        style={{ width: 220 }}
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={e => setDocFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={docUploading}>
                      {docUploading ? <span className="spinner-border spinner-border-sm" /> : '+ Upload'}
                    </button>
                  </form>
                  {docError && <div className="text-danger small mt-2">{docError}</div>}
                </div>
              </div>

              {/* Module assignment */}
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-0">Module Access</h6>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {selectedModules.size} of {registry.length} modules enabled
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button className="btn btn-outline-secondary btn-sm" onClick={selectAll}>All</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={selectNone}>None</button>
                      {modulesSuccess && <span className="text-success small">Saved</span>}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleModulesSave}
                        disabled={modulesSaving}
                      >
                        {modulesSaving ? <span className="spinner-border spinner-border-sm" /> : 'Save Modules'}
                      </button>
                    </div>
                  </div>
                  {modulesError && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{modulesError}</div>}

                  {modulesLoading ? (
                    <div className="text-center py-3"><span className="spinner-border spinner-border-sm" /></div>
                  ) : (
                    byCategory.map(({ cat, modules }) => (
                      <div key={cat} className="mb-3">
                        <div
                          className="text-muted fw-semibold mb-2"
                          style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          {cat}
                        </div>
                        <div className="row g-1">
                          {modules.map(m => (
                            <div key={m.key} className="col-md-6">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`mod-${m.key}`}
                                  checked={selectedModules.has(m.key)}
                                  onChange={() => toggleModule(m.key)}
                                />
                                <label className="form-check-label" htmlFor={`mod-${m.key}`} style={{ fontSize: 13 }}>
                                  {m.label}
                                  <div className="text-muted" style={{ fontSize: 11 }}>{m.description}</div>
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── AI Usage Overview ── */}
      <div className="card mt-4">
        <div className="card-body">
          <h6 className="card-title mb-3">🤖 AI Usage — This Month</h6>
          {aiUsageLoading ? (
            <div className="text-muted text-center py-3"><span className="spinner-border spinner-border-sm me-2" />Loading…</div>
          ) : (
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Company</th>
                  <th className="text-end">Today</th>
                  <th className="text-end">This Month</th>
                  <th style={{ width: 180 }}>Daily limit</th>
                  <th style={{ width: 180 }}>Monthly limit</th>
                </tr>
              </thead>
              <tbody>
                {aiUsageData.map(row => {
                  const dailyPct   = row.aiDailyBudgetUsd   ? Math.min(100, (row.dailySpendUsd   / row.aiDailyBudgetUsd)   * 100) : null;
                  const monthlyPct = row.aiMonthlyBudgetUsd ? Math.min(100, (row.monthlySpendUsd / row.aiMonthlyBudgetUsd) * 100) : null;
                  const barColor = (pct) => pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success';
                  return (
                    <tr key={row.id}>
                      <td style={{ fontSize: 13 }}>{row.name}</td>
                      <td className="text-end" style={{ fontSize: 13 }}>
                        {row.dailySpendUsd > 0 ? `$${row.dailySpendUsd.toFixed(3)}` : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-end" style={{ fontSize: 13 }}>
                        {row.monthlySpendUsd > 0 ? `$${row.monthlySpendUsd.toFixed(2)}` : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        {row.aiDailyBudgetUsd ? (
                          <div>
                            <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                              <span>${row.dailySpendUsd.toFixed(3)}</span>
                              <span className="text-muted">${row.aiDailyBudgetUsd}</span>
                            </div>
                            <div className="progress" style={{ height: 5 }}>
                              <div className={`progress-bar ${barColor(dailyPct)}`} style={{ width: `${dailyPct}%` }} />
                            </div>
                          </div>
                        ) : <span className="text-muted" style={{ fontSize: 12 }}>unlimited</span>}
                      </td>
                      <td>
                        {row.aiMonthlyBudgetUsd ? (
                          <div>
                            <div className="d-flex justify-content-between" style={{ fontSize: 11 }}>
                              <span>${row.monthlySpendUsd.toFixed(2)}</span>
                              <span className="text-muted">${row.aiMonthlyBudgetUsd}</span>
                            </div>
                            <div className="progress" style={{ height: 5 }}>
                              <div className={`progress-bar ${barColor(monthlyPct)}`} style={{ width: `${monthlyPct}%` }} />
                            </div>
                          </div>
                        ) : <span className="text-muted" style={{ fontSize: 12 }}>unlimited</span>}
                      </td>
                    </tr>
                  );
                })}
                {aiUsageData.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-3" style={{ fontSize: 13 }}>No AI usage recorded yet.</td></tr>
                )}
              </tbody>
              {aiUsageData.length > 0 && (
                <tfoot className="table-light">
                  <tr>
                    <td className="fw-semibold" style={{ fontSize: 13 }}>Total</td>
                    <td className="text-end fw-semibold" style={{ fontSize: 13 }}>
                      ${aiUsageData.reduce((s, r) => s + r.dailySpendUsd, 0).toFixed(3)}
                    </td>
                    <td className="text-end fw-semibold" style={{ fontSize: 13 }}>
                      ${aiUsageData.reduce((s, r) => s + r.monthlySpendUsd, 0).toFixed(2)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>

      <ConfirmModal
        show={!!deleteDocTarget}
        title="Delete document?"
        message={`Remove "${deleteDocTarget?.label}" from ${selectedCompany?.name}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteDocument}
        onCancel={() => setDeleteDocTarget(null)}
      />
    </div>
  );
}

export default SuperAdminPage;
